import {
  HttpClient,
  HttpErrorResponse,
} from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, share, tap } from 'rxjs';
import { ApiStatus } from './model/api-status';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { environment } from '@environments/environment';
import { DateTime } from 'luxon';
import { SignalsService } from '@shared/services/signals/signals.service';

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  constructor(
    private http: HttpClient,
    private errorLogService: ErrorLogService,
    private signalsService: SignalsService,
  ) { }


  isLoadingProgressBar = signal<boolean>(false);

  checkApiStatus(): Observable<ApiStatus[]> {
    const date = DateTime.now().toISO();
    sessionStorage.setItem('apiStatusChecked', JSON.stringify(date));

    return combineLatest([
      this.getAPIFlightDataSummary(),
      this.postAPICrewUser(),
      this.postAPICrewDetail(),
      this.getAPIFlightStats()
    ]).pipe(
      map(([flightData, crewUser, crewDetail, flightStats]) => {
        let apiStatusArr = [
          flightData,
          crewUser,
          crewDetail,
          flightStats
        ];
        sessionStorage.setItem('apiStatus', JSON.stringify(apiStatusArr));
        return apiStatusArr;
      }),
      tap(() => {
        this.isLoadingProgressBar.set(false);
      })
    );
  }

  /**
   * Handles the form submission event.
   * 
   * This method performs the following actions:
   * 1. Clears the active employee number cache.
   * 2. Emits the current employee number value through the `_empNumberObs` observable.
   * 3. Sets the `isEumulatingEmpNumActive$` observable to `true`.
   * 4. Navigates the router to the `/flights` route.
   * 
   * @returns {void}
   */
  eumlatePilotFor(empNum: string): void {
    this.clearActiveEmpNumCache();
    this.signalsService.isEumulatingEmpNumActive$.set(true);
    this.signalsService._empNumberObs.next(empNum);
  }

  /**
   * Resets the admin component state by performing the following actions:
   * - Clears the cache for the active employee number being reviewed.
   * - Emits a new value for the employee number observable back to users account.
   * - Sets the active employee number admin status to false and clears admin notification
   * icon.
   *
   * @returns {void}
   */
  resetEmulatedPilot(): void {
    this.clearActiveEmpNumCache();
    this.signalsService._empNumberObs.next(this.signalsService._authEmployeeNumber.getValue());
    this.signalsService.isEumulatingEmpNumActive$.set(false);
  }


  getAPIFlightDataSummary(): Observable<ApiStatus> {
    const date = DateTime.now().toMillis();
    const apiName = 'Pilot Flight Data API';
    const url = `${environment.apiUrl}/summary/000000/0001/1`;
    return this.http.get(url, { observe: 'response' })
      .pipe(
        map((response) => {
          if (!response) {
            return {
              endpoint: apiName,
              status: 0,
              endTime: 0
            }
          }
          return {
            endpoint: apiName,
            status: response.status,
            endTime: DateTime.now().toMillis() - date
          }
        }),
        catchError((err) => this.handleError(err, apiName)),
      );
  }



  postAPICrewUser(): Observable<ApiStatus> {
    const date = DateTime.now().toMillis();
    const apiName = 'Crew User API';
    const url = `${environment.crewBasicUrl}`;
    const postArr = [''];
    return this.http.post(url, postArr, { observe: 'response' }).pipe(
      map((response) => {
        if (!response) {
          return {
            endpoint: apiName,
            status: 0,
            endTime: 0
          }
        }
        return {
          endpoint: apiName,
          status: response.status,
          endTime: DateTime.now().toMillis() - date
        }
      }),
      catchError((err) => this.handleError(err, apiName))
    );
  }

  postAPICrewDetail(): Observable<ApiStatus> {
    const date = DateTime.now().toMillis();
    const apiName = 'Member Info API';
    const url = `${environment.crewDetailUrl}`;
    const post = { searchTerm: '000000', userId: '000000', request: 0 };
    return this.http.post(url, post, { observe: 'response' }).pipe(
      map(response => {
        if (!response) {
          return {
            endpoint: apiName,
            status: 0,
            endTime: 0
          }
        }
        return {
          endpoint: apiName,
          status: response.status,
          endTime: DateTime.now().toMillis() - date
        }
      }),
      catchError((err) => this.handleError(err, apiName))
    );
  }

  getAPIFlightStats(): Observable<ApiStatus> {
    const date = DateTime.now().toMillis();
    const apiName = 'Pilot Flight Stats API';
    const url = `${environment.apiUrl}/Stats/flightTime/00000`;
    return this.http.get(url, { observe: 'response' })
      .pipe(
        map((response) => {
          if (!response) {
            return {
              endpoint: apiName,
              status: 0,
              endTime: 0
            }
          }
          return {
            endpoint: apiName,
            status: response.status,
            endTime: DateTime.now().toMillis() - date
          }
        }),
        catchError((err) => this.handleError(err, apiName)),
      );
  }

  /**
   * Clears all entries in the local storage except for specific keys.
   * The keys that are not removed are:
   * - 'appSettings'
   * - 'logbook.version.data'
   * - 'theme'
   * - 'table.prevSelectedPeriod'
   * - 'isOpenedInWebView'
   * 
   * This method iterates through the local storage in reverse order and removes
   * any key that is not in the exclusion list.  This is to clear all flight information
   * and other data that is not needed when switching back to users own logbook.
   */
  clearActiveEmpNumCache(): void {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && 
        key !== 'appSettings' &&
        key !== 'logbook.version.data' &&
        key !== 'theme' &&
        key !== 'calendar.prevSelectedMonth' &&
        key !== 'isOpenedInWebView' &&
        key !== 'member.loggedin'
      ) {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Clears all items from the session storage except for specific keys.
   * 
   * This method iterates through all keys in the session storage and removes
   * each key-value pair unless the key is one of the following:
   * - '0-https://logbook.alliedpilots.org', auth token
   * - 'expiresAt', auth token expiration
   * - 'apiStatusChecked', last time API status was checked
   * - 'apiStatus', last API status
   * 
   * @remarks
   * This function is useful for clearing session data while preserving
   * essential information required for the application to function correctly.
   */
  clearSessionStorage(): void {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key && 
        key !== '0-https://logbook.alliedpilots.org' &&
        key !== 'expiresAt' && 
        key !== 'apiStatusChecked' &&
        key !== 'apiStatus'
      ) {
        sessionStorage.removeItem(key);
      }
    }
  }

  clearErrorLogs(): void {
    sessionStorage.removeItem('errorLog');
  }


  handleError(error: HttpErrorResponse, api: string): Observable<ApiStatus> {
    this.errorLogService.logError({ error: error, message: `Error in ${api}` });
    return of(
      {
        endpoint: api,
        status: error.status,
        endTime: 0
      }
    )
  }
}

